import { motion, useMotionValue, useReducedMotion, useSpring } from 'motion/react';
import { useRef } from 'react';
import type { CSSProperties, ReactNode } from 'react';
import { useI18n } from '../i18n';

export const EASE = [0.22, 1, 0.36, 1] as const;
export const EASE_INOUT = [0.65, 0, 0.35, 1] as const;

function cx(...parts: Array<string | undefined | false>) {
  return parts.filter(Boolean).join(' ');
}

/* ─────────────────────────────────────────────
   Reveal — scroll-linked line: enters from below,
   exits above, reverses when scrolling back
───────────────────────────────────────────── */
export function Reveal({
  children,
  delay = 0,
  duration: _duration = 0.9,
  inline = false,
  className,
}: {
  children: ReactNode;
  delay?: number;
  duration?: number;
  inline?: boolean;
  className?: string;
}) {
  const reduced = useReducedMotion();
  const display = inline ? 'inline-block' : 'block';

  return (
    <span
      className={cx(!reduced && 'scroll-linked scroll-linked--line', className)}
      style={{ display, '--scroll-stagger': `${delay * 8}%` } as CSSProperties}
    >
      {children}
    </span>
  );
}

/* ─────────────────────────────────────────────
   FadeIn — scroll-linked rise: in on the way
   down, out on the way up (view timeline)
───────────────────────────────────────────── */
export function FadeIn({
  children,
  delay = 0,
  y: _y = 24,
  className,
}: {
  children: ReactNode;
  delay?: number;
  y?: number;
  className?: string;
}) {
  const reduced = useReducedMotion();
  return (
    <div
      className={cx(!reduced && 'scroll-linked', className)}
      style={{ '--scroll-stagger': `${delay * 8}%` } as CSSProperties}
    >
      {children}
    </div>
  );
}

/* ─────────────────────────────────────────────
   ScaleIn — scroll-linked pop: scales up as it
   enters, eases out above (view timeline)
───────────────────────────────────────────── */
export function ScaleIn({
  children,
  delay = 0,
  className,
}: {
  children: ReactNode;
  delay?: number;
  className?: string;
}) {
  const reduced = useReducedMotion();
  return (
    <div
      className={cx(!reduced && 'scroll-linked scroll-linked--scale', className)}
      style={{ '--scroll-stagger': `${delay * 8}%` } as CSSProperties}
    >
      {children}
    </div>
  );
}

/* ─────────────────────────────────────────────
   Magnetic — element leans toward the cursor
───────────────────────────────────────────── */
export function Magnetic({
  children,
  strength = 0.32,
  className,
}: {
  children: ReactNode;
  strength?: number;
  className?: string;
}) {
  const reduced = useReducedMotion();
  const ref = useRef<HTMLDivElement>(null);
  const x = useMotionValue(0);
  const y = useMotionValue(0);
  const sx = useSpring(x, { stiffness: 180, damping: 16, mass: 0.4 });
  const sy = useSpring(y, { stiffness: 180, damping: 16, mass: 0.4 });

  const onMove = (e: React.MouseEvent) => {
    if (reduced) return;
    const rect = ref.current?.getBoundingClientRect();
    if (!rect) return;
    x.set((e.clientX - rect.left - rect.width / 2) * strength);
    y.set((e.clientY - rect.top - rect.height / 2) * strength);
  };

  return (
    <motion.div
      ref={ref}
      className={className}
      onMouseMove={onMove}
      onMouseLeave={() => {
        x.set(0);
        y.set(0);
      }}
      style={{ display: 'inline-block', x: sx, y: sy }}
    >
      {children}
    </motion.div>
  );
}

/* ─────────────────────────────────────────────
   SectionHead — recurring editorial section
   header: hairline, mono index, big title
───────────────────────────────────────────── */
export function SectionHead({
  index,
  label,
  title,
  aside,
}: {
  index: string;
  label: string;
  title: ReactNode;
  aside?: string;
}) {
  const { locale } = useI18n();
  const isAr = locale === 'ar';

  return (
    <div className="pt-2 mb-16 md:mb-24">
      <div
        aria-hidden
        className="scroll-draw mb-6"
        style={{ height: 1, background: 'var(--line)' }}
      />
      <div className="flex items-baseline justify-between gap-6 mb-6 md:mb-9">
        <span
          className="mono-label"
          style={{ color: 'var(--muted)', letterSpacing: '0.04em' }}
        >
          {label}
        </span>
        {aside ? (
          <span className="mono-label hidden sm:block" style={{ color: 'var(--faint)' }}>
            {aside}
          </span>
        ) : null}
      </div>
      <h2
        className="display"
        style={{
          color: 'var(--ink)',
          fontSize: isAr ? 'var(--display-section-ar)' : 'var(--display-section)',
          lineHeight: isAr ? 1.2 : 1.05,
          maxWidth: isAr ? '16ch' : '14ch',
          letterSpacing: '-0.03em',
          fontWeight: 680,
        }}
      >
        {title}
      </h2>
      <span className="sr-only">
        {index} {label}
      </span>
    </div>
  );
}
